import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import LanguageSelector, { voicesByLanguage } from './components/LanguageSelector';
import AudioPlayer from './components/AudioPlayer';
import useTTS from './useTTS';
import { observer } from 'mobx-react';
import { createUseStyles } from 'react-jss';
import { useTheme } from '@mui/material/styles';
import { useStore } from '../../store/store';

interface IAIVoiceProps {
  onSelect?: (aSrc: string, aFileName?: string) => void;
}

export default observer(function AiVoice({ onSelect }: IAIVoiceProps) {
  const { mediaLibraryStore, pageStore, courseStore } = useStore();
  const { uploadFileToFolder } = mediaLibraryStore;
  const { coursePage } = pageStore;
  const { pageProperties } = courseStore;
  const [language, setLanguage] = useState('');
  const [textInput, setTextInput] = useState('');
  const [voice, setVoice] = useState('');
  const theme = useTheme();
  const { fetchTTS, audioFile, audioUrl, audioDuration, loading } = useTTS();
  const langSelection = 'languageSelection';
  const voiceSelection = 'voiceSelection';

  const handleLanguageChange = (aLang: string) => {
    setLanguage(aLang);
    localStorage.setItem(langSelection, JSON.stringify(aLang));

    if (aLang !== language) {
      const voice = voicesByLanguage[aLang][0];
      setVoice(voice);
    }
  };

  const handleVoiceChange = (aVoice: string) => {
    setVoice(aVoice);
    localStorage.setItem(voiceSelection, JSON.stringify(aVoice));
  };

  const handleGenerateVoice = () => {
    if (textInput.length < 1) return;
    let courseID: string | number = 'unknown';
    let pageID: string | number = 'unknown';
    if (coursePage) {
      courseID = coursePage.courseID;
      pageID = coursePage.coursePageID;
    } else if (pageProperties?.descriptions?.items?.title?.option?.value) {
      courseID = pageProperties.courseID;
      pageID = pageProperties.coursePageID;
    }
    fetchTTS(textInput, courseID, pageID, language, voice, 'audio-48khz-192kbitrate-mono-mp3');
  };

  const handleUploadVoice = () => {
    if (audioFile === null) return;
    uploadFileToFolder(audioFile, 'AIVoice').then((test) => {
      if (!test) return;
      const keys = Object.keys(test);

      if (keys.length < 1 || test[keys[0]].status === false) {
        //SnackbarUtils.warning('Upload failed. Try again.');
        return;
      }
      if (onSelect) {
        onSelect(test[keys[0]].url, test[keys[0]].mediaTitle);
      }
    });
  };

  useEffect(() => {
    const lsLangSelected = localStorage.getItem(langSelection);
    const languageSelected: string = lsLangSelected ? JSON.parse(lsLangSelected) : 'en-GB';

    const lsVoiceSelected = localStorage.getItem(voiceSelection);
    let voiceSelected: string = lsVoiceSelected ? JSON.parse(lsVoiceSelected) : '';

    if (!voicesByLanguage[languageSelected]?.includes(voiceSelected)) {
      voiceSelected = voicesByLanguage[languageSelected][0];
    }
    setLanguage(languageSelected);
    setVoice(voiceSelected);
  }, []);

  const useStyles = createUseStyles({
    loader: {
      width: 'fit-content',
      fontSize: '18px',
      clipPath: 'inset(0 1.5ch 0 0)',
      animation: '$loadingAnimation 2s steps(4) infinite',
      '&:before': {
        content: '"Loading..."',
      },
    },
    '@keyframes loadingAnimation': {
      to: {
        clipPath: 'inset(0 -0.5ch 0 0)',
      },
    },
  });
  const classes = useStyles();

  return (
    <>
      <div
        style={{
          padding: theme.spacing(2),
          gap: theme.spacing(2),
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          flexGrow: 1,
        }}
      >
        <div style={{ display: 'flex', flexGrow: 1, gap: theme.spacing(2) }}>
          <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexGrow: 1, position: 'relative' }}>
              <textarea
                style={{ backgroundColor: theme.palette.bgZero.main, padding: theme.spacing(2), flexGrow: 1, color: theme.palette.text.primary, resize: 'none', border: 0, outline: 0, fontSize: 14 }}
                placeholder='Write the text you wish to have generated into AI voice'
                value={textInput}
                onChange={(e: any) => setTextInput(e.target.value)}
              />
              {loading && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <div className={classes.loader}></div>
                </div>
              )}
            </div>
            <AudioPlayer audioSrc={audioUrl} handleSetVolume={() => void 0} duration={audioDuration} />
          </div>
          <div style={{ flexBasis: 360, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <LanguageSelector language={language} setLanguage={handleLanguageChange} voice={voice} setVoice={handleVoiceChange} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button color='primary' variant='contained' style={{ alignSelf: 'baseline', minWidth: 170 }} onClick={handleGenerateVoice} disabled={loading}>
                  GENERATE AI VOICE
                </Button>
                <Button variant='newSecondary' style={{ alignSelf: 'baseline', minWidth: 170, fontWeight: 700 }} disabled={audioUrl.length < 1 || loading} onClick={handleUploadVoice}>
                  APPLY TO PAGE
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
});
